import { useEffect, useState, useRef, ChangeEvent } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "../supabaseClient";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Paperclip, Send, ArrowLeft, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";

interface MentorAttachment {
  url: string;
  name: string;
  type?: string | null;
  size?: number | null;
}

interface MentorMessage {
  id: string;
  user_id: string;
  mentor_id: string;
  sender_type: string;
  message: string | null;
  attachments: MentorAttachment[] | null;
  created_at: string;
}

interface LocationState {
  mentorName?: string;
}

export default function MentorChatPage() {
  const { t } = useTranslation();
  const PROFILE_UPDATED_EVENT = "innergem:profile-updated";
  const { mentorId } = useParams<{ mentorId: string }>();
  const location = useLocation();
  const state = (location.state || {}) as LocationState;
  const navigate = useNavigate();
  const { toast } = useToast();

  const [mentorName, setMentorName] = useState<string | null>(state.mentorName || null);
  const [messages, setMessages] = useState<MentorMessage[]>([]);
  const [input, setInput] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [subscriptionTier, setSubscriptionTier] = useState<string | null>(null);
  const [mentorMessageCredits, setMentorMessageCredits] = useState<number>(0);
  const [isTrialUser, setIsTrialUser] = useState(false);
  const [mentorPassUntil, setMentorPassUntil] = useState<Date | null>(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState<MentorMessage | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeletingConversation, setIsDeletingConversation] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const loadMembership = async (userId: string) => {
      const { data: userProfile } = await supabase
        .from("user_profiles")
        .select("subscription_tier, mentor_message_credits, trial_code, mentor_deepdive_unlimited_until")
        .eq("id", userId)
        .maybeSingle();

      if (!userProfile) return;

      const rawTier = (userProfile as any).subscription_tier as string | null;
      const tier = rawTier ? rawTier.toLowerCase().replace(/\s+/g, "_") : null;
      setSubscriptionTier(tier);

      const credits = (userProfile as any).mentor_message_credits as number | null;
      setMentorMessageCredits(credits ?? 0);

      const trialCode = ((userProfile as any).trial_code as string | null) ?? null;
      const normalizedCode = (trialCode || "").toUpperCase();
      setIsTrialUser(!!trialCode && (normalizedCode.startsWith("GEMTRIAL") || normalizedCode === "NAS@INNERGEM30"));

      const untilRaw = ((userProfile as any).mentor_deepdive_unlimited_until as string | null) ?? null;
      if (untilRaw) {
        const d = new Date(untilRaw);
        setMentorPassUntil(Number.isFinite(d.getTime()) ? d : null);
      } else {
        setMentorPassUntil(null);
      }
    };

    const load = async () => {
      if (!mentorId) return;
      setIsLoading(true);

      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        setIsLoading(false);
        return;
      }

      setCurrentUserId(user.id);

      // Load current user membership + mentor credit info
      await loadMembership(user.id);

      // Fetch mentor name if not passed via navigation state
      if (!mentorName) {
        const { data: mentorProfile } = await supabase
          .from("user_profiles")
          .select("full_name")
          .eq("id", mentorId)
          .maybeSingle();

        const fullName = (mentorProfile?.full_name as string | undefined)?.trim();
        if (fullName && fullName.length > 0) {
          setMentorName(fullName);
        }
      }

      const { data, error } = await supabase
        .from("mentor_messages")
        .select("id, user_id, mentor_id, sender_type, message, attachments, created_at")
        .eq("user_id", user.id)
        .eq("mentor_id", mentorId)
        .order("created_at", { ascending: true });

      if (error) {
        console.error("Failed to load mentor messages", error);
      } else {
        const mapped: MentorMessage[] = (data || []).map((row: any) => ({
          id: row.id,
          user_id: row.user_id,
          mentor_id: row.mentor_id,
          sender_type: row.sender_type,
          message: row.message ?? null,
          attachments: (row.attachments as MentorAttachment[] | null) || null,
          created_at: row.created_at,
        }));
        setMessages(mapped);
      }

      setIsLoading(false);
    };

    const onProfileUpdated = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;
      await loadMembership(user.id);
    };

    load();
    window.addEventListener(PROFILE_UPDATED_EVENT, onProfileUpdated);

    return () => {
      window.removeEventListener(PROFILE_UPDATED_EVENT, onProfileUpdated);
    };
  }, [mentorId, mentorName]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const isRestrictedMembership = () => {
    if (isTrialUser) return true;
    if (!subscriptionTier) return false;
    const tier = subscriptionTier.toLowerCase();
    return (
      tier === "beginner" ||
      tier === "beginner_monthly" ||
      tier === "beginner_yearly" ||
      tier === "intermediate" ||
      tier === "intermediate_monthly" ||
      tier === "intermediate_yearly" ||
      tier === "upper_intermediate" ||
      tier === "upper_intermediate_monthly" ||
      tier === "upper_intermediate_yearly"
    );
  };

  const hasActiveMentorPass = () => {
    if (!mentorPassUntil) return false;
    return mentorPassUntil.getTime() > Date.now();
  };

  const hasMentorCreditAvailable = () => hasActiveMentorPass() || mentorMessageCredits > 0;

  const isSendBlockedByMembership = () => {
    if (!isRestrictedMembership()) return false;
    return !hasMentorCreditAvailable();
  };

  const isUpperIntermediateMembership = () => {
    if (!subscriptionTier) return false;
    const tier = subscriptionTier.toLowerCase();
    return (
      tier === "upper_intermediate" ||
      tier === "upper_intermediate_monthly" ||
      tier === "upper_intermediate_yearly"
    );
  };

  const hasUpperIntermediateDiscount = isUpperIntermediateMembership() && !isTrialUser;

  const paymentLink = hasUpperIntermediateDiscount
    ? "https://buy.stripe.com/5kQbJ18V2dDd8F1dFMeUU05"
    : "https://buy.stripe.com/aFa28rb3a7eP2gDcBIeUU04";

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    const selectedFiles = Array.from(e.target.files);
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

    const oversizedFiles = selectedFiles.filter((file) => file.size > MAX_FILE_SIZE);

    if (oversizedFiles.length > 0) {
      toast({
        title: t("mentorChat.fileTooLargeTitle", {
          defaultValue: "File too large",
        }),
        description: t("mentorChat.fileTooLargeDescription", {
          defaultValue: "Each attachment must be 10MB or smaller.",
        }),
        variant: "destructive",
      });

      const validFiles = selectedFiles.filter((file) => file.size <= MAX_FILE_SIZE);
      if (validFiles.length === 0) {
        e.target.value = "";
        setFiles([]);
        return;
      }

      setFiles(validFiles);
      return;
    }

    setFiles(selectedFiles);
  };

  const openDeleteMessageDialog = (msg: MentorMessage) => {
    setMessageToDelete(msg);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDeleteMessage = async () => {
    if (!messageToDelete) return;
    setIsDeleting(true);
    try {
      const { error } = await supabase
        .from("mentor_messages")
        .delete()
        .eq("id", messageToDelete.id);

      if (error) {
        console.error("Failed to delete mentor message", error);
        toast({
          title: t("mentorChat.deleteFailedTitle", { defaultValue: "Delete failed" }),
          description: t("mentorChat.deleteFailedDesc", {
            defaultValue: "Could not delete this message. Please try again.",
          }),
          variant: "destructive",
        });
        return;
      }

      setMessages((prev) => prev.filter((m) => m.id !== messageToDelete.id));
      toast({
        title: t("mentorChat.deleteSuccessTitle", { defaultValue: "Message deleted" }),
        description: t("mentorChat.deleteSuccessDesc", {
          defaultValue: "This message has been permanently removed.",
        }),
      });
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setMessageToDelete(null);
    }
  };

  const handleDeleteConversation = async () => {
    if (!mentorId || !currentUserId) return;
    setIsDeletingConversation(true);
    try {
      const { error } = await supabase
        .from("mentor_messages")
        .delete()
        .eq("user_id", currentUserId)
        .eq("mentor_id", mentorId);

      if (error) {
        console.error("Failed to delete mentor conversation", error);
        toast({
          title: t("mentorChat.deleteConversationFailedTitle", { defaultValue: "Delete failed" }),
          description: t("mentorChat.deleteConversationFailedDesc", {
            defaultValue: "Could not delete this conversation. Please try again.",
          }),
          variant: "destructive",
        });
        return;
      }

      setMessages([]);
      toast({
        title: t("mentorChat.deleteConversationSuccessTitle", { defaultValue: "Conversation deleted" }),
        description: t("mentorChat.deleteConversationSuccessDesc", {
          defaultValue: "All messages in this conversation have been removed.",
        }),
      });
    } finally {
      setIsDeletingConversation(false);
    }
  };

  const handleSend = async () => {
    if (!mentorId) return;
    const trimmedInput = input.trim();
    // A message can be text-only, attachment-only, or both.
    if (!trimmedInput && files.length === 0) {
      toast({
        title: t("mentorChat.creditRequiresTextAndAttachmentTitle", {
          defaultValue: "Text or attachment required",
        }),
        description: t("mentorChat.creditRequiresTextAndAttachmentDesc", {
          defaultValue: "Please enter a message or attach at least one file (photos/PDF/docs).",
        }),
        variant: "destructive",
      });
      return;
    }

    // Block send when membership requires credit but none is available
    if (isSendBlockedByMembership()) {
      return;
    }

    setIsSending(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) return;

      const attachments: MentorAttachment[] = [];

      if (files.length > 0) {
        for (const [index, file] of files.entries()) {
          const extIndex = file.name.lastIndexOf(".");
          const ext = extIndex >= 0 ? file.name.slice(extIndex) : "";
          const safeFileName = `${Date.now()}_${index}_${Math.random().toString(36).slice(2, 8)}${ext}`;
          const path = `${user.id}/${mentorId}/${safeFileName}`;

          const { error: uploadError } = await supabase
            .storage
            .from("mentor-attachments")
            .upload(path, file);

          if (uploadError) {
            console.error("Upload error", uploadError);
            continue;
          }

          const { data: publicUrlData } = supabase
            .storage
            .from("mentor-attachments")
            .getPublicUrl(path);

          const publicUrl = publicUrlData.publicUrl;

          attachments.push({
            url: publicUrl,
            name: file.name,
            type: file.type || null,
            size: file.size,
          });
        }
      }

      // If user is sending attachment-only and all uploads failed, block creating an empty message.
      if (!trimmedInput && attachments.length === 0) {
        toast({
          title: t("mentorChat.uploadFailedTitle", { defaultValue: "Upload failed" }),
          description: t("mentorChat.uploadFailedDesc", {
            defaultValue: "Your attachments could not be uploaded. Please try again.",
          }),
          variant: "destructive",
        });
        return;
      }

      const { data, error } = await supabase
        .from("mentor_messages")
        .insert({
          user_id: user.id,
          mentor_id: mentorId,
          sender_type: "user",
          message: trimmedInput || null,
          attachments: attachments.length > 0 ? attachments : null,
          is_read_by_mentor: false,
        })
        .select()
        .single();

      if (error) {
        console.error("Failed to send mentor message", error);
      } else if (data) {
        const newMessage: MentorMessage = {
          id: data.id,
          user_id: data.user_id,
          mentor_id: data.mentor_id,
          sender_type: data.sender_type,
          message: data.message ?? null,
          attachments: (data.attachments as MentorAttachment[] | null) || null,
          created_at: data.created_at,
        };

        setMessages((prev) => [...prev, newMessage]);
        setInput("");
        setFiles([]);

        // Fire-and-forget: notify mentor via email about the new message
        try {
          const notifyRes = await supabase.functions.invoke("notify-mentor-message", {
            body: {
              mentorId,
              userId: user.id,
              messageId: data.id,
            },
          });

          // Helpful for production debugging (push delivery depends on VAPID + subscription).
          if (notifyRes?.error) {
            console.error("notify-mentor-message returned error", notifyRes.error);
          } else {
            console.log("notify-mentor-message result", notifyRes?.data);
          }
        } catch (notifyError) {
          console.error("Failed to trigger mentor notification", notifyError);
        }

        // 每发送一条新消息就消耗 1 个 credit（仅对受限会员生效；Mentor Pass 期间不扣）
        if (isRestrictedMembership() && !hasActiveMentorPass() && mentorMessageCredits > 0) {
          const newCredits = mentorMessageCredits - 1;
          setMentorMessageCredits(newCredits);

          await supabase
            .from("user_profiles")
            .update({ mentor_message_credits: newCredits })
            .eq("id", user.id);
        }
      }
    } finally {
      setIsSending(false);
    }
  };

  const title = mentorName
    ? t("mentorChat.title", { name: mentorName })
    : t("mentorChat.titleFallback", { defaultValue: "Mentor Chat" });

  return (
    <div className="container max-w-3xl mx-auto pt-10 pb-6 px-4 flex flex-col gap-4 h-[calc(100vh-5rem)]">
      <div className="flex items-center mb-1">
        <Button
          type="button"
          variant="ghost"
          className="mr-1 rounded-full px-3 h-8 text-sm flex items-center gap-1"
          onClick={() => navigate("/mentor")}
          aria-label={t("mentorChat.backToMentors", { defaultValue: "Back to Mentor Deep Dive" })}
        >
          <ArrowLeft className="w-4 h-4" />
          <span>
            {t("mentorChat.backToMentors", {
              defaultValue: "Back to Mentor Deep Dive",
            })}
          </span>
        </Button>
      </div>

      <Card className="flex-none">
        <CardHeader>
          <CardTitle className="text-xl font-semibold">
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          {t("mentorChat.subtitle", {
            defaultValue:
              "Messages and attachments you send here are saved. Whenever you return to this mentor, you will see your full conversation history.",
          })}
          {messages.length > 0 && (
            <div className="mt-3 flex justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleDeleteConversation}
                disabled={isDeletingConversation}
              >
                <Trash2 className="w-3 h-3 mr-1" />
                {t("mentorChat.deleteConversation", { defaultValue: "Delete conversation" })}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="flex-1 min-h-0 flex flex-col">
        <CardContent className="flex-1 min-h-0 pt-4 pb-2 px-2 overflow-y-auto space-y-3 no-scrollbar">
          {isLoading ? (
            <p className="text-center text-sm text-muted-foreground">
              {t("common.loading", { defaultValue: "Loading conversation..." })}
            </p>
          ) : messages.length === 0 ? null : (
            messages.map((msg) => {
              const isMine = currentUserId
                ? msg.sender_type === "user"
                  ? msg.user_id === currentUserId
                  : msg.mentor_id === currentUserId
                : msg.sender_type === "user";

              return (
                <div
                  key={msg.id}
                  className={`group relative flex flex-col gap-1 ${
                    isMine ? "items-end" : "items-start"
                  }`}
                >
                  {msg.message && (
                    <div
                      className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap shadow-sm ${
                        isMine
                          ? "bg-primary text-primary-foreground"
                          : "bg-primary/5 text-primary"
                      }`}
                    >
                      {msg.message}
                    </div>
                  )}
                  {msg.attachments && msg.attachments.length > 0 && (
                    <div
                      className={`max-w-[80%] flex flex-col gap-1 mt-1 ${
                        isMine ? "items-end" : "items-start"
                      }`}
                    >
                      {msg.attachments.map((file, index) => (
                        <a
                          key={`${msg.id}-att-${index}`}
                          href={file.url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-2 text-xs text-purple-900 bg-purple-50 border border-purple-100 rounded-xl px-3 py-2 hover:bg-purple-100 shadow-sm"
                        >
                          <Paperclip className="w-3 h-3" />
                          <span className="truncate max-w-[200px] font-medium">
                            {file.name}
                          </span>
                        </a>
                      ))}
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => openDeleteMessageDialog(msg)}
                    className={`absolute top-0 ${
                      isMine ? "left-0" : "right-0"
                    } -mt-2 opacity-0 group-hover:opacity-100 transition-opacity text-xs text-muted-foreground flex items-center gap-1 bg-background/80 rounded-full px-2 py-0.5 shadow-sm border`}
                  >
                    <Trash2 className="w-3 h-3" />
                    {t("common.delete", { defaultValue: "Delete" })}
                  </button>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </CardContent>

        <div className="relative border-t px-4 py-3 space-y-2">
          <div className="flex items-center gap-3">
            <label className="inline-flex items-center gap-1 text-xs text-muted-foreground cursor-pointer">
              <Paperclip className="w-4 h-4" />
              <span>
                {t("mentorChat.uploadLabel", {
                  defaultValue: "Attach files (images, PDF, docs)",
                })}
              </span>
              <Input
                type="file"
                multiple
                className="hidden"
                accept="image/*,.pdf,.doc,.docx,.txt"
                onChange={handleFileChange}
                disabled={isSendBlockedByMembership()}
              />
            </label>
            {files.length > 0 && (
              <span className="text-xs text-muted-foreground">
                {t("mentorChat.selectedFiles", {
                  count: files.length,
                  defaultValue: "{{count}} file(s) selected",
                })}
              </span>
            )}
          </div>

          <div className="flex items-end gap-2">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t("mentorChat.inputPlaceholder", {
                defaultValue: "Write a message to your mentor...",
              })}
              className="min-h-[60px] max-h-[160px] resize-none overflow-y-auto no-scrollbar"
              disabled={isSendBlockedByMembership()}
            />
            <Button
              type="button"
              onClick={handleSend}
              disabled={
                isSending ||
                (!input.trim() && files.length === 0) ||
                isSendBlockedByMembership()
              }
              className="shrink-0 h-10 w-10 rounded-full p-0 flex items-center justify-center"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>

          {isSendBlockedByMembership() && (
            <div
              className="absolute inset-0 z-10 flex items-center justify-center bg-background/80 backdrop-blur-sm cursor-pointer"
              onClick={() => {
                window.open(paymentLink, "_blank", "noopener,noreferrer");
              }}
            >
              <div className="px-4 py-3 rounded-2xl bg-white/90 border shadow-sm text-center max-w-xs">
                <p className="text-sm font-semibold text-primary mb-1">
                  {t("mentorChat.unlockDeepQuestionOverlay", {
                    defaultValue: "解锁与导师的一次深度询问",
                  })}
                </p>
                <p className="text-[11px] text-muted-foreground">
                  {hasUpperIntermediateDiscount
                    ? t("mentorChat.unlockPriceUpper", {
                        defaultValue: "Upper Intermediate 会员专属价格：SGD 28.99（20% 优惠）",
                      })
                    : t("mentorChat.unlockPriceOther", {
                        defaultValue: "其它会员价格：SGD 35.99",
                      })}
                </p>
              </div>
            </div>
          )}
        </div>
      </Card>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("mentorChat.deleteConfirmTitle", { defaultValue: "Delete this message?" })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("mentorChat.deleteConfirmDesc", {
                defaultValue:
                  "This will permanently remove this message for both you and your mentor. This action cannot be undone.",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>
              {t("common.cancel", { defaultValue: "Cancel" })}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDeleteMessage}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("common.delete", { defaultValue: "Delete" })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
